import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Truck, Gem, Star, Zap, Heart, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, getEntity } from "../lib/api";
import { cldSquare } from "../lib/img";
import { SEO, breadcrumbLd, productLd } from "../components/SEO";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { gaViewItem } from "../lib/analytics";
import NotFound from "./NotFound";
import { LoadError } from "../components/LoadError";
import { renderMd, stripMdText } from "../lib/renderMd";
import { RelatedProducts } from "../components/RelatedProducts";

// Ring-only buying options (rendered above Personalize on ring categories).
const RING_SIZES = Array.from({ length: 11 }, (_, i) => i + 4); // US 4 → 14
const METAL_COLORS = ["Yellow Gold", "White Gold", "Rose Gold"];


function Stars({ rating = 0, size = 14, testid }) {
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          className={n <= Math.round(rating) ? "fill-black text-black" : "text-zinc-300"}
        />
      ))}
    </div>
  );
}

function ReviewsBlock({ productSlug }) {
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [form, setForm] = useState({ name: "", email: "", rating: 5, title: "", comment: "" });
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get(`/reviews`, { params: { product_slug: productSlug } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [productSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.comment.trim()) return toast.error("Name and review are required");
    setBusy(true);
    try {
      await api.post(`/reviews`, { ...form, product_slug: productSlug });
      toast.success("Thanks! Your review is pending admin approval and will appear once reviewed.");
      setForm({ name: "", email: "", rating: 5, title: "", comment: "" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div data-testid="product-reviews">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b-2 border-black pb-4">
        <div>
          <h3 className="font-display text-2xl font-black tracking-tight text-black">Customer Reviews</h3>
          <div className="mt-2 flex items-center gap-3">
            <Stars rating={data.average} size={16} testid="reviews-avg-stars" />
            <span className="text-sm text-zinc-600" data-testid="reviews-average">
              {data.count > 0 ? `${data.average.toFixed(1)} out of 5 · ${data.count} review${data.count === 1 ? "" : "s"}` : "No reviews yet"}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((s) => !s)}
          data-testid="write-review-btn"
          className="border-2 border-black bg-white px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors duration-200 hover:bg-black hover:text-white"
        >
          {showForm ? "Cancel" : "Write a Review"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-6 space-y-4 border-2 border-black bg-white p-6" data-testid="review-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Your Name</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name-input" className="mt-1 w-full border-2 border-black bg-white px-3 py-2 text-sm text-black focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Email (optional, not published)</span>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="review-email-input" className="mt-1 w-full border-2 border-black bg-white px-3 py-2 text-sm text-black focus:outline-none" />
            </label>
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Rating</span>
            <div className="mt-2 flex items-center gap-1" data-testid="review-rating-select">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm({ ...form, rating: n })}
                  data-testid={`review-star-${n}`}
                  aria-label={`Rate ${n} stars`}
                  className="p-1"
                >
                  <Star size={22} className={n <= form.rating ? "fill-black text-black" : "text-zinc-300"} />
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Review Title (optional)</span>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="review-title-input" className="mt-1 w-full border-2 border-black bg-white px-3 py-2 text-sm text-black focus:outline-none" placeholder="Ice cold, hits hard" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Your Review</span>
            <textarea required rows={4} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="review-comment-input" className="mt-1 w-full border-2 border-black bg-white px-3 py-2 text-sm text-black focus:outline-none" placeholder="Tell us what you think..." />
          </label>
          <button type="submit" disabled={busy} data-testid="submit-review-btn" className="border-2 border-black bg-black px-8 py-3 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors duration-200 hover:bg-white hover:text-black disabled:opacity-50">
            {busy ? "Posting…" : "Post Review"}
          </button>
        </form>
      )}

      <div className="mt-8 space-y-6" data-testid="reviews-list">
        {data.reviews.length === 0 ? (
          <p className="text-sm text-zinc-500">Be the first to review this piece.</p>
        ) : (
          data.reviews.map((r) => (
            <article key={r.id} className="border-b border-black/10 pb-6" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Stars rating={r.rating} size={13} />
                  <span className="text-xs font-bold uppercase tracking-widest text-black">{r.name}</span>
                </div>
                <span className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
              </div>
              {r.title && <h4 className="mt-3 font-display text-base font-black text-black">{r.title}</h4>}
              <p className="mt-2 text-sm leading-relaxed text-zinc-700">{r.comment}</p>
            </article>
          ))
        )}
      </div>
    </div>
  );
}

export default function ProductPage() {
  const { categorySlug, productSlug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [variant, setVariant] = useState("");
  const [ringSize, setRingSize] = useState("");
  const [metalColor, setMetalColor] = useState("");
  const [personalization, setPersonalization] = useState("");
  const [panels, setPanels] = useState(null);
  const [openPanel, setOpenPanel] = useState(null);
  const [tab, setTab] = useState("details");
  const { addItem } = useCart();
  const { isWishlisted, toggle } = useWishlist();

  useEffect(() => {
    setNotFound(false);
    setLoadFailed(false);
    setProduct(null);
    setActiveImg(0);
    setVariant("");
    setRingSize("");
    setMetalColor("");
    setPersonalization("");
    setTab("details");
    let cancelled = false;
    getEntity(`/products/${productSlug}`).then((res) => {
      if (cancelled) return;
      if (res.missing) return setNotFound(true);
      if (res.error) return setLoadFailed(true);
      setProduct(res.data);
      if (res.data.variants?.length) setVariant(res.data.variants[0]);
      // Live overlay — the main payload may be CDN-cached; this uncached
      // call guarantees the displayed price + stock status are current.
      api
        .get("/products-live", { params: { slugs: productSlug } })
        .then((lr) => {
          const live = lr.data?.items?.[productSlug];
          if (live) setProduct((p) => (p && p.slug === productSlug ? { ...p, ...live } : p));
        })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [productSlug]);

  useEffect(() => {
    api.get("/site/product-panels").then((r) => setPanels(r.data)).catch(() => {});
  }, []);

  // Preload every product image (square, 1:1) as soon as the product loads,
  // so clicking a thumbnail swaps instantly instead of triggering a fetch.
  useEffect(() => {
    if (!product?.images?.length) return;
    product.images.forEach((src) => {
      const img = new Image();
      img.src = cldSquare(src, 1200);
    });
  }, [product]);

  // GA4 view_item — fire once per unique product slug shown.
  useEffect(() => {
    if (product?.slug) gaViewItem(product);
  }, [product?.slug]);

  const totalImages = product?.images?.length || 0;
  const goPrev = () => setActiveImg((i) => (i - 1 + totalImages) % totalImages);
  const goNext = () => setActiveImg((i) => (i + 1) % totalImages);

  if (notFound) return <NotFound />;
  if (loadFailed) return <LoadError />;
  if (!product) return <div className="min-h-[60vh]" />;

  const catName = categorySlug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const hasVariants = (product.variants || []).length > 0;
  // Rings ship in a size + metal colour, so both are picked on the page
  // instead of being typed into the free-text Personalize box.
  const isRing = /(^|-)rings?$/.test(categorySlug) || /(^|-)rings?$/.test(product.category_slug || "");
  const cartVariant = isRing
    ? [hasVariants && variant, ringSize && `US ${ringSize}`, metalColor].filter(Boolean).join(" · ")
    : hasVariants ? variant : "";
  const ringIncomplete = isRing && (!ringSize || !metalColor);

  const doAdd = () => {
    addItem(product, 1, cartVariant, personalization);
    toast.success(`${product.name}${cartVariant ? ` (${cartVariant})` : ""} added to cart`);
  };

  const buyNow = () => {
    addItem(product, 1, cartVariant, personalization);
    navigate("/cart");
  };

  // Built from the canonical site URL, never window.location — the prerenderer
  // runs on 127.0.0.1, and a localhost link baked into the static HTML looks
  // like a broken store to Google's crawlers.
  const siteBase = (process.env.REACT_APP_SITE_URL || process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const canonicalProductUrl = `${siteBase}/${categorySlug}/${product.slug}`;

  return (
    <div data-testid="product-page" className="mx-auto w-full max-w-7xl overflow-x-hidden px-4 py-16 sm:px-8">
      <SEO
        title={product.meta_title || `${product.name} | VVS Lab Jewelry`}
        description={product.meta_description || stripMdText(product.description).slice(0, 160)}
        image={product.images?.[0]}
        jsonLd={[
          productLd(product),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: catName, path: `/${categorySlug}` },
            { name: product.name, path: `/${categorySlug}/${product.slug}` },
          ]),
        ]}
      />
      <nav className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-black hover:underline">Home</Link> <span className="mx-2">/</span>
        <Link to={`/${categorySlug}`} className="hover:text-black hover:underline">{catName}</Link> <span className="mx-2">/</span>
        <span className="text-black">{product.name}</span>
      </nav>

      <div className="mt-10 grid gap-12 lg:grid-cols-2">
        <div className="min-w-0">
          <div
            className="relative aspect-square w-full overflow-hidden border-2 border-black bg-zinc-100"
            data-testid="product-image-viewer"
          >
            {product.images?.map((img, i) => (
              <img
                key={i}
                src={cldSquare(img, 1200)}
                alt={`${product.name} view ${i + 1}`}
                width="1200"
                height="1200"
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : "auto"}
                decoding="async"
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-200 ${i === activeImg ? "opacity-100" : "opacity-0"}`}
              />
            ))}
            {totalImages > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Previous image"
                  data-testid="product-image-prev"
                  className="absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center border-2 border-black bg-white/90 text-black shadow-sm transition-colors duration-200 hover:bg-black hover:text-white"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Next image"
                  data-testid="product-image-next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center border-2 border-black bg-white/90 text-black shadow-sm transition-colors duration-200 hover:bg-black hover:text-white"
                >
                  <ChevronRight size={20} />
                </button>
                <span
                  data-testid="product-image-counter"
                  className="absolute bottom-3 right-3 border-2 border-black bg-white/90 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-black"
                >
                  {activeImg + 1} / {totalImages}
                </span>
              </>
            )}
          </div>
          {product.images?.length > 1 && (
            <div
              className="mt-4 flex flex-nowrap gap-2 overflow-x-auto pb-1 sm:gap-3 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              data-testid="product-thumbs"
            >
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`product-thumb-${i}`}
                  aria-label={`View image ${i + 1}`}
                  className={`h-16 w-16 shrink-0 overflow-hidden border-2 transition-colors duration-200 sm:h-20 sm:w-20 ${i === activeImg ? "border-black" : "border-black/20"}`}
                >
                  <img src={cldSquare(img, 200)} alt={`${product.name} view ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">{catName}</p>
          <h1 className="mt-4 break-words font-display text-2xl font-black tracking-tight text-black sm:text-3xl lg:text-4xl lg:tracking-tighter" data-testid="product-title">{product.name}</h1>
          <div className="mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-2">
            <span className="text-3xl font-extrabold text-black" data-testid="product-price">${product.price.toFixed(2)}</span>
            {product.compare_at_price && <span className="text-lg text-zinc-400 line-through">${product.compare_at_price.toFixed(2)}</span>}
            {product.compare_at_price && (
              <span className="bg-black px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                Save ${(product.compare_at_price - product.price).toFixed(0)}
              </span>
            )}
          </div>
          <div className="mt-8 text-base leading-relaxed text-zinc-600" data-testid="product-short-description">{renderMd(product.description)}</div>

          {hasVariants && (
            <div className="mt-8" data-testid="product-variants">
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                {product.variant_label || "Option"} · <span className="text-black">{variant}</span>
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {product.variants.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVariant(v)}
                    data-testid={`variant-${v}`}
                    className={`min-w-[56px] border-2 px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors duration-200 ${
                      variant === v ? "border-black bg-black text-white" : "border-black/20 text-black hover:border-black"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isRing && (
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="ring-options">
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-500">Ring Size <span className="text-black">(US)</span></span>
                <select
                  value={ringSize}
                  onChange={(e) => setRingSize(e.target.value)}
                  data-testid="ring-size-select"
                  className="mt-2 w-full appearance-none border-2 border-black bg-white bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22black%22 stroke-width=%222%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-[length:16px] bg-[right_12px_center] bg-no-repeat px-3 py-3 pr-10 text-sm font-bold uppercase tracking-widest text-black focus:outline-none"
                >
                  <option value="">Select size</option>
                  {RING_SIZES.map((s) => (
                    <option key={s} value={s}>{`US ${s}`}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-500">Metal Color</span>
                <select
                  value={metalColor}
                  onChange={(e) => setMetalColor(e.target.value)}
                  data-testid="metal-color-select"
                  className="mt-2 w-full appearance-none border-2 border-black bg-white bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22black%22 stroke-width=%222%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-[length:16px] bg-[right_12px_center] bg-no-repeat px-3 py-3 pr-10 text-sm font-bold uppercase tracking-widest text-black focus:outline-none"
                >
                  <option value="">Select color</option>
                  {METAL_COLORS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </label>
            </div>
          )}

          <div className="mt-8" data-testid="product-personalization">
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                Personalize <span className="ml-1 text-zinc-400">(optional)</span>
              </span>
              <textarea
                rows={3}
                value={personalization}
                onChange={(e) => setPersonalization(e.target.value.slice(0, 500))}
                data-testid="product-personalization-input"
                placeholder={isRing ? "Engraving, initials or any special request" : "Mention your personalization — size, metal color, engraving, etc."}
                className="mt-2 w-full resize-none border-2 border-black bg-white px-3 py-3 text-sm text-black placeholder-zinc-400 focus:outline-none"
              />
              <span className="mt-1 block text-right text-[10px] text-zinc-400">{personalization.length}/500</span>
            </label>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              onClick={doAdd}
              disabled={!product.in_stock || (hasVariants && !variant) || ringIncomplete}
              data-testid="add-to-cart-btn"
              className="flex-1 border-2 border-black bg-white py-5 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors duration-200 hover:bg-black hover:text-white disabled:opacity-40"
            >
              {product.in_stock ? "Add To Cart" : "Sold Out"}
            </button>
            <button
              onClick={buyNow}
              disabled={!product.in_stock || (hasVariants && !variant) || ringIncomplete}
              data-testid="buy-now-btn"
              className="flex flex-1 items-center justify-center gap-2 border-2 border-black bg-black py-5 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors duration-200 hover:bg-white hover:text-black disabled:opacity-40"
            >
              <Zap size={14} />
              Buy Now
            </button>
            <button
              type="button"
              onClick={() => toggle(product.slug)}
              aria-label={isWishlisted(product.slug) ? "Remove from wishlist" : "Add to wishlist"}
              data-testid="product-wishlist-btn"
              className="flex items-center justify-center border-2 border-black bg-white px-5 py-5 text-black transition-colors duration-200 hover:bg-black hover:text-white"
            >
              <Heart size={16} className={isWishlisted(product.slug) ? "fill-[#1e40af] text-[#1e40af]" : ""} />
            </button>
          </div>

          <a
            href={`https://api.whatsapp.com/send/?phone=919799348600&text=${encodeURIComponent(
              `Hi VVS Lab Jewelry, I'm interested in "${product.name}" — ${canonicalProductUrl}`
            )}&type=phone_number&app_absent=0`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="product-whatsapp-btn"
            className="mt-3 flex w-full items-center justify-center gap-2 border-2 border-black bg-[#25D366] py-5 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors duration-200 hover:bg-black hover:text-white"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M20.52 3.48A11.86 11.86 0 0 0 12 0C5.37 0 .01 5.37.01 12c0 2.11.55 4.17 1.6 5.98L0 24l6.24-1.63A11.94 11.94 0 0 0 12 24c6.63 0 11.99-5.37 11.99-12 0-3.2-1.25-6.21-3.47-8.52ZM12 21.82a9.83 9.83 0 0 1-5.02-1.38l-.36-.21-3.7.97.99-3.6-.24-.37A9.79 9.79 0 0 1 2.18 12C2.18 6.58 6.58 2.18 12 2.18c2.62 0 5.09 1.02 6.94 2.87A9.78 9.78 0 0 1 21.82 12c0 5.42-4.4 9.82-9.82 9.82Zm5.66-7.34c-.31-.16-1.83-.9-2.11-1s-.49-.16-.7.16-.8 1-.98 1.21-.36.24-.67.08c-.31-.16-1.31-.48-2.49-1.53a9.34 9.34 0 0 1-1.72-2.13c-.18-.31 0-.47.14-.63.14-.14.31-.36.47-.54.16-.18.21-.31.31-.52.11-.21.05-.39-.03-.54-.08-.16-.7-1.68-.96-2.3-.25-.6-.51-.52-.7-.53h-.6c-.21 0-.54.08-.83.39s-1.09 1.06-1.09 2.6 1.11 3.02 1.27 3.23 2.2 3.36 5.33 4.72c.75.32 1.33.51 1.78.66.75.24 1.43.21 1.97.13.6-.09 1.83-.75 2.09-1.47.26-.72.26-1.34.18-1.47-.08-.13-.28-.21-.59-.36Z" />
            </svg>
            Chat on WhatsApp
          </a>

          <div className="mt-12 grid gap-4 border-t-2 border-black pt-8 sm:grid-cols-3">
            {[
              [Gem, "VVS Stones", "Passes diamond tester"],
              [Truck, "Free Shipping", "Worldwide, insured"],
              [ShieldCheck, "Lifetime Shine", "Guarantee included"],
            ].map(([Icon, title, sub]) => (
              <div key={title} className="flex items-start gap-3">
                <Icon size={18} className="mt-0.5 text-black" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-black">{title}</p>
                  <p className="mt-1 text-xs text-zinc-500">{sub}</p>
                </div>
              </div>
            ))}
          </div>

          {panels && (
            <div className="mt-10 border-t-2 border-black" data-testid="product-panels">
              {[
                ["jewelry_care", "Jewelry Care"],
                ["contact_us", "Contact Us"],
                ["shipping_delivery", "Shipping & Delivery"],
                ["warranty", "VVSLAB Warranty"],
              ].map(([key, label]) => {
                const isOpen = openPanel === key;
                const content = (panels[key] || "").trim();
                if (!content) return null;
                return (
                  <div key={key} className="border-b-2 border-black" data-testid={`product-panel-${key}`}>
                    <button
                      type="button"
                      onClick={() => setOpenPanel(isOpen ? null : key)}
                      aria-expanded={isOpen}
                      data-testid={`product-panel-toggle-${key}`}
                      className="flex w-full items-center justify-between gap-4 py-4 text-left transition-colors duration-200 hover:bg-zinc-50"
                    >
                      <span className="text-xs font-bold uppercase tracking-[0.25em] text-black">{label}</span>
                      <ChevronDown
                        size={18}
                        className={`shrink-0 text-black transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {isOpen && (
                      <div
                        className="pb-6 pr-2 text-sm leading-relaxed text-zinc-700"
                        data-testid={`product-panel-content-${key}`}
                      >
                        {renderMd(content)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Details + Reviews */}
      <section className="mt-20 border-t-2 border-black pt-10" data-testid="product-details-section">
        <div className="flex gap-1 border-b-2 border-black">
          <button
            onClick={() => setTab("details")}
            data-testid="tab-details"
            className={`px-6 py-3 text-xs font-bold uppercase tracking-[0.25em] transition-colors duration-200 ${
              tab === "details" ? "bg-black text-white" : "text-zinc-500 hover:text-black"
            }`}
          >
            Product Details
          </button>
          <button
            onClick={() => setTab("reviews")}
            data-testid="tab-reviews"
            className={`px-6 py-3 text-xs font-bold uppercase tracking-[0.25em] transition-colors duration-200 ${
              tab === "reviews" ? "bg-black text-white" : "text-zinc-500 hover:text-black"
            }`}
          >
            Reviews
          </button>
        </div>

        <div className="pt-8">
          {tab === "details" ? (
            <div className="max-w-3xl" data-testid="product-details-content">
              {product.details ? (
                renderMd(product.details)
              ) : (
                <div className="text-sm text-zinc-600">{renderMd(product.description)}</div>
              )}
              {product.sku && (
                <table className="mt-8 w-full max-w-md border-2 border-black text-sm" data-testid="product-sku-table">
                  <tbody>
                    <tr>
                      <th className="border-r-2 border-black bg-black px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.25em] text-white">
                        SKU
                      </th>
                      <td className="px-4 py-3 font-mono text-sm text-black" data-testid="product-sku-value">
                        {product.sku}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <ReviewsBlock productSlug={product.slug} />
          )}
        </div>
        <RelatedProducts slug={product.slug} limit={8} />
      </section>
    </div>
  );
}
